import React from 'react';
import styles from './SideDrawer.module.css';
import type { ReadinessResult } from './cftcRulesEngine';

type ReadinessBadgeProps = {
  readiness: ReadinessResult | null;
  scale?: Array<{ min: number; max: number; label: string }>;
  definitions?: Array<{ title: string; items: string[] }>;
};

const READINESS_INTERPRETATION = [
  'Offsides = positioning extreme (who is trapped). High means crowding.',
  'Participation = open interest expanding. High means fresh involvement.',
  'Pressure = positioning building against price. High means fragility rising.',
  'Trigger = price confirmation (breakout or MA). High means catalyst active.',
  'High score with Offsides + Trigger: squeeze risk active.',
  'High Offsides, low Trigger: setup building, not fired.',
  'High Trigger, low Offsides: flow-driven move, not a squeeze.',
  'Context signal, not trade timing.',
];

const ReadinessBadge = ({ readiness, scale, definitions }: ReadinessBadgeProps) => {
  if (!readiness) {
    return null;
  }

  return (
    <div className={styles.readinessBadge} data-onboard="readiness-summary">
      <span>
        Trade Readiness: {Math.round(readiness.score)}/100 — {readiness.label}
      </span>
      <span
        className={styles.readinessInfoIcon}
        aria-label="Readiness breakdown"
        tabIndex={0}
        data-onboard="readiness-breakdown"
      >
        ?
        <span className={styles.readinessTooltip}>
          <span className={styles.readinessTooltipTitle}>Trade Readiness</span>
          <span className={styles.readinessTooltipRowGrid}>
            {scale?.length ? (
              <span className={styles.readinessTooltipSection}>
                <span className={styles.readinessTooltipTitle}>Scale</span>
                {scale.map((entry) => (
                  <span key={entry.label} className={styles.readinessTooltipRow}>
                    <span className={styles.readinessTooltipLabel}>{entry.label}</span>
                    <span>
                      {entry.min}-{entry.max}
                    </span>
                  </span>
                ))}
              </span>
            ) : null}
            <span className={styles.readinessTooltipSection}>
              <span className={styles.readinessTooltipTitle}>Current</span>
              {readiness.components.map((component) => (
                <span key={component.name} className={styles.readinessTooltipRow}>
                  <span className={styles.readinessTooltipLabel}>{component.name}</span>
                  <span>
                    {Math.round(component.score)} / {Math.round(component.max)}
                  </span>
                </span>
              ))}
            </span>
          </span>
          <span className={styles.readinessTooltipRowGrid}>
            <span className={styles.readinessTooltipSection}>
              <span className={styles.readinessTooltipTitle}>How to read</span>
              <div className={styles.readinessDefinitionList}>
                {READINESS_INTERPRETATION.map((item) => (
                  <div key={item} className={styles.readinessDefinitionItem}>
                    {item}
                  </div>
                ))}
              </div>
            </span>
            {definitions?.length ? (
              <span className={styles.readinessTooltipSection}>
                <span className={styles.readinessTooltipTitle}>Definitions</span>
                {definitions.map((definition) => (
                  <span key={definition.title} className={styles.readinessDefinition}>
                    <span className={styles.readinessDefinitionTitle}>{definition.title}</span>
                    <div className={styles.readinessDefinitionList}>
                      {definition.items.map((item) => (
                        <div key={item} className={styles.readinessDefinitionItem}>
                          {item}
                        </div>
                      ))}
                    </div>
                  </span>
                ))}
              </span>
            ) : null}
          </span>
        </span>
      </span>
    </div>
  );
};

export default ReadinessBadge;
